import React, { useState, useEffect } from 'react';
import { Select, SelectItem, SelectSection } from '@heroui/react';
import type { Selection } from '@react-types/shared';
import { SortOptions } from '@/shared/types/common';

interface SelectSortProps {
  sections?: SortOptions[];
  onSelectionChange?: (keys: Selection) => void;
  defaultValue?: string;
}

const sortOptions: SortOptions[] = [
  {
    id: '1',
    title: 'By date',
    options: [
      {
        label: 'newest first',
        value: 'By date: newest first',
      },
      {
        label: 'oldest first',
        value: 'By date: oldest first',
      },
    ],
  },
  {
    id: '2',
    title: 'Price',
    options: [
      {
        label: 'lowest first',
        value: 'Price: lowest first',
      },
      {
        label: 'highest first',
        value: 'Price: highest first',
      },
    ],
  },
  {
    id: '3',
    title: 'Reviews',
    options: [
      {
        label: 'lowest rated first',
        value: 'Reviews: lowest rated first',
      },
      {
        label: 'highest rated first',
        value: 'Reviews: highest rated first',
      },
    ],
  },
];

export const SelectSort = ({
  sections = sortOptions,
  onSelectionChange,
  defaultValue = 'default',
}: SelectSortProps) => {
  const [value, setValue] = useState<string | number>(defaultValue);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const getDisplayText = (selectedValue: string) => {
    if (selectedValue === 'default') return <>Default</>;

    for (const section of sections) {
      const option = section.options.find((opt) => opt.value === selectedValue);
      if (option) {
        return (
          <>
            {section.title}: <span className="font-bold">{option.label}</span>
          </>
        );
      }
    }
    return <span>{selectedValue}</span>;
  };

  const handleSelectionChange = (keys: Selection) => {
    const newValue = new Set(keys).values().next().value || 'default';
    setValue(newValue);
    onSelectionChange?.(keys);
  };

  return (
    <Select
      aria-label="Sort by"
      variant="bordered"
      selectedKeys={[value]}
      onSelectionChange={handleSelectionChange}
      disallowEmptySelection
      startContent={<p className="text-grey-tertiary font-medium text-base text-left">Sort by:</p>}
      renderValue={(items) =>
        items.map((item) => (
          <div
            key={item.key}
            className="flex items-center gap-2 min-w-fit font-semibold leading-[1.5] tracking-[-1.1%]"
          >
            <span className="font-medium text-black">{getDisplayText(item.key as string)}</span>
          </div>
        ))
      }
      classNames={{
        base: 'min-w-[320px]',
        trigger: 'shadow-none border-0 cursor-pointer',
        innerWrapper: 'w-fit pr-5 ml-auto',
        value: 'border-0 w-fit',
      }}
      className="w-fit"
      popoverProps={{
        placement: 'bottom-start',
        classNames: {
          content: 'shadow-[0px_-2px_6.6px_0px_#92929240] rounded-[4px] w-[320px]',
        },
      }}
    >
      <SelectSection showDivider>
        <SelectItem key="default" textValue="Default">
          Default
        </SelectItem>
      </SelectSection>
      <>
        {sections?.map((section) => (
          <SelectSection
            showDivider
            key={section.id}
            classNames={
              {
                // divider: 'last:hidden',
              }
            }
          >
            {section.options.map((option) => (
              <SelectItem key={option.value} textValue={option.value}>
                <span>{`${section.title}:`}</span>
                <span className="ml-1 font-medium text-black-pure">{option.label}</span>
              </SelectItem>
            ))}
          </SelectSection>
        ))}
      </>
    </Select>
  );
};
